import { IconButton, SingleSelectDropdown, Slider } from "@carbon6/c6-frontend-design";
import { useEffect, useState } from "react";
import type { ViewerTrack } from "../../domain/viewerTypes";
import { sampleTcp, type TcpInspection } from "../../feature/tcpPose";
import type { Vec3 } from "../../feature/rotationMath";
import styles from "./TcpCoordinates.module.css";

type Props = {
  tracks: readonly ViewerTrack[];
  seconds: number;
  inspection: TcpInspection | null;
  active: boolean;
  onInspection: (next: TcpInspection | null) => void;
};

function coordinates(point: Vec3 | null | undefined): string {
  return point == null
    ? "—"
    : point.map((value, i) => `${"XYZ"[i]} ${value.toFixed(4)}`).join(" · ");
}

/** 재생바 옆의 고정 좌표와 합성 회전 제어. 카메라 자세는 바꾸지 않는다. */
export function TcpCoordinates({ tracks, seconds, inspection, active, onInspection }: Props) {
  const [selection, setSelection] = useState<string | null>(null);
  const [lapStart, setLapStart] = useState<number | null>(null);
  const lap = lapStart !== null;
  const track =
    tracks.find((item) => item.id === (inspection?.trackId ?? selection)) ??
    tracks.find((item) => item.emphasized) ??
    tracks[0];
  const points = track === undefined ? null : sampleTcp(track, seconds, inspection);
  const inspecting = inspection !== null;
  const axis = inspection?.axis ?? "X";
  const degrees = inspection?.degrees ?? 0;

  useEffect(() => {
    if (lapStart === null || inspection === null || !active) return;
    let frame = 0;
    const start = lapStart;
    const tick = (now: number) => {
      const next = Math.min(360, (now - start) / 12);
      onInspection({ ...inspection, degrees: next });
      if (next < 360) frame = requestAnimationFrame(tick);
      else setLapStart(null);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [lapStart, active, inspection, onInspection]);

  const role = track?.tcp?.role;
  // 고정 기준 변환도 출력 pose 를 옮긴다. BE `assess_tcp` 처럼 그 근거를 함께 본다.
  const reference = role?.reference_transform;
  const fixed = reference?.kind === "fixed";
  const evidence =
    role === undefined
      ? []
      : [
          role.tcp.origin.evidence,
          role.tcp.transform.evidence,
          role.tcp.applied_correction.evidence,
          ...(fixed ? [reference.evidence] : []),
        ];
  const state = evidence.some((item) => item.official_search.status === "conflicting")
    ? "근거 상충"
    : evidence.some((item) => item.basis === "inferred")
      ? "추론 후보"
      : evidence.length === 0 ||
          evidence.some((item) => item.basis === null || item.basis === "assumed")
        ? "근거 미확인"
        : "근거 입력됨";

  return (
    <section className={styles.panel} aria-label="TCP 좌표" data-tcp-inspection={inspecting}>
      <div className={styles.controls}>
        <SingleSelectDropdown
          size="sm"
          label="좌표를 표시할 손과 역할"
          value={track?.id ?? null}
          options={tracks.map((item) => ({ value: item.id, label: item.label }))}
          onValueChange={(id) => {
            setSelection(id);
            setLapStart(null);
            onInspection(null);
          }}
        />
        <span>
          기준 {role?.reference_frame ?? "—"}
          {fixed ? ` (원본 ${reference?.source_frame ?? "—"} 에서 변환)` : ""} · m · {state}
        </span>
        <IconButton
          size="sm"
          type="button"
          variant="surface"
          text={inspecting ? "점검 종료" : "360° 점검"}
          disabled={points?.closed == null}
          onClick={() => {
            setLapStart(null);
            onInspection(
              inspecting || track === undefined
                ? null
                : { trackId: track.id, seconds, axis: "X", degrees: 0 },
            );
          }}
        />
      </div>
      <output className={styles.values} aria-label="현재 파지 중심 G">
        G · 현재 파지 중심: {coordinates(points?.grip)}
      </output>
      {inspecting || points?.grip == null ? (
        <>
          <output className={styles.values} aria-label="원본 기준점 S">
            S · 원본 기준점: {coordinates(points?.source)}
          </output>
          <output className={styles.values} aria-label="닫힘 TCP C">
            C · 닫힘 TCP: {coordinates(points?.closed)}
          </output>
        </>
      ) : null}
      {inspecting ? (
        <>
          <div className={styles.controls}>
            <SingleSelectDropdown
              size="sm"
              label="원본 도구 회전축"
              value={axis}
              options={["X", "Y", "Z"].map((value) => ({ value, label: `원본 도구 ${value}` }))}
              onValueChange={(value) => {
                setLapStart(null);
                if (inspection !== null)
                  onInspection({ ...inspection, axis: value as TcpInspection["axis"], degrees: 0 });
              }}
            />
            <Slider
              className={styles.slider}
              aria-label="S 중심 회전 각도"
              min={0}
              max={360}
              step={1}
              value={degrees}
              onValueChange={(value) => {
                setLapStart(null);
                if (inspection !== null) onInspection({ ...inspection, degrees: value });
              }}
            />
            <span>{degrees.toFixed(0)}°</span>
            <IconButton
              text={lap ? "회전 정지" : "한 바퀴"}
              size="sm"
              type="button"
              variant="surface"
              onClick={() => setLapStart(lap ? null : performance.now())}
            />
            <IconButton
              text="초기화"
              size="sm"
              type="button"
              variant="surface"
              onClick={() => {
                setLapStart(null);
                if (inspection !== null) onInspection({ ...inspection, degrees: 0 });
              }}
            />
          </div>
          <p>
            합성 좌표 · {inspection.seconds.toFixed(3)}s의 S와 그리퍼 값을 고정했습니다. 실제 TCP
            확인 근거가 아닙니다.
          </p>
        </>
      ) : null}
      {points?.grip == null ? (
        <p>현재 그리퍼 값 또는 TCP를 사용할 수 없습니다. 열린 손 모양은 표시용입니다.</p>
      ) : null}
      <p>G는 표시 모델의 손끝 중점입니다. 실제 TCP 확인 결과가 아닙니다.</p>
    </section>
  );
}
